// src/components/pcr/AutoSave.tsx
import React, { useState, useEffect } from 'react';
import { useForm } from '../../contexts/FormContext';

interface AutoSaveProps {
  interval?: number; // Auto-save interval in milliseconds
  idleThreshold?: number; // Time in milliseconds before user is considered idle
}

const AutoSave: React.FC<AutoSaveProps> = ({
  interval = 60000, // Default: 1 minute
  idleThreshold = 300000, // Default: 5 minutes
}) => {
  const { formData, saveForm, lastSaved, hasUnsavedChanges } = useForm();
  const [nextSave, setNextSave] = useState<number>(interval);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveStatus, setSaveStatus] = useState<string>('');
  const [isActive, setIsActive] = useState<boolean>(true);
  const [lastActivity, setLastActivity] = useState<Date>(new Date());

  // Track user activity
  useEffect(() => {
    const updateActivity = (): void => {
      setIsActive(true);
      setLastActivity(new Date());
    };

    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart'
    ];

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    // Set up idle timer
    const idleTimer = setTimeout(() => {
      setIsActive(false);
    }, idleThreshold);

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      clearTimeout(idleTimer);
    };
  }, [idleThreshold]);

  // Calculate time until next save
  useEffect(() => {
    if (nextSave <= 0 || !isActive || !hasUnsavedChanges) {
      return;
    }

    const timer = setTimeout(() => {
      setNextSave(prev => Math.max(0, prev - 1000));
    }, 1000);

    return () => clearTimeout(timer);
  }, [nextSave, isActive, hasUnsavedChanges]);

  // Auto-save effect
  useEffect(() => {
    // Only set up auto-save if there are unsaved changes
    if (!hasUnsavedChanges) {
      return;
    }
    
    const autoSaveTimer = setTimeout(async () => {
      // Only save if there's data to save and user is active (or just became inactive)
      if (formData && formData.userId && (isActive || 
          (new Date().getTime() - lastActivity.getTime() < 10000))) {
        try {
          setIsSaving(true);
          setSaveStatus('Saving...');
          await saveForm();
          setSaveStatus('Saved successfully');
          
          // Reset timer for next auto-save
          setNextSave(interval);
        } catch (error) {
          console.error('Auto-save failed:', error);
          setSaveStatus('Auto-save failed');
        } finally {
          setIsSaving(false);
        }
      } else {
        // Just reset the timer without saving if user is inactive
        setNextSave(interval);
      }
    }, nextSave);

    return () => clearTimeout(autoSaveTimer);
  }, [formData, saveForm, nextSave, interval, isActive, lastActivity, hasUnsavedChanges]);

  // Clear status message after a delay
  useEffect(() => {
    if (saveStatus === 'Saved successfully') {
      const clearTimer = setTimeout(() => {
        setSaveStatus('');
      }, 3000);
      
      return () => clearTimeout(clearTimer);
    }
  }, [saveStatus]);
  
  // Handle manual save trigger
  const handleManualSave = async (): Promise<void> => {
    if (!hasUnsavedChanges) return;
    
    try {
      setIsSaving(true);
      setSaveStatus('Saving...');
      await saveForm();
      setSaveStatus('Saved successfully');
      // Reset timer for next auto-save
      setNextSave(interval);
    } catch (error) {
      console.error('Manual save failed:', error);
      setSaveStatus('Save failed');
    } finally {
      setIsSaving(false);
    }
  };

  // Format the next save time as MM:SS
  const formatTimeRemaining = (): string => {
    const minutes = Math.floor(nextSave / 60000);
    const seconds = Math.floor((nextSave % 60000) / 1000);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  // Format the last saved date
  const formatLastSaved = (date: Date | null): string => {
    if (!date) return 'Not saved yet';
    
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    // If less than a minute ago, show "just now"
    if (diff < 60000) {
      return 'just now';
    }
    
    // If less than an hour ago, show minutes
    if (diff < 3600000) {
      const minutes = Math.floor(diff / 60000);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    }
    
    // Otherwise show the time
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed bottom-24 right-4 z-40 bg-white rounded-md shadow-md p-2 text-xs text-gray-600 flex flex-col items-end">
      <div className="flex items-center space-x-2">
        <div className={`h-2 w-2 rounded-full ${
          isSaving ? 'bg-yellow-500 animate-pulse' : 
          (saveStatus === 'Saved successfully' ? 'bg-green-500' : 
          (saveStatus === 'Save failed' ? 'bg-red-500' : 
          (hasUnsavedChanges ? 'bg-yellow-500' : 'bg-green-500')))
        }`}></div>
        <span>
          {isSaving ? 'Saving...' : 
           (saveStatus ? saveStatus : 
           (hasUnsavedChanges ? 
             (isActive ? `Auto-save in ${formatTimeRemaining()}` : 'Paused while inactive') : 
             'All changes saved'))}
        </span>
        
        {hasUnsavedChanges && !isSaving && (
          <button 
            onClick={handleManualSave}
            className="text-blue-600 hover:text-blue-800 ml-2 underline text-xs"
          >
            Save now
          </button>
        )}
      </div>
      
      <div className="text-gray-500 mt-1 flex items-center justify-between w-full">
        <span>Last saved: {formatLastSaved(lastSaved)}</span>
        {!isActive && hasUnsavedChanges && (
          <span className="text-yellow-600 ml-2">
            • Idle
          </span>
        )}
      </div>
    </div>
  );
};

export default AutoSave;